import React, { useState, useRef } from "react"
import { Button } from "frontend/components/interaction"
import Field from "./Field"

import styles from "./Form.module.css"
import { ERRORS } from "./constants"

/**
<Form /> can create fields automatically via props.fields, which takes an object like so:
{ 
  field1: {
    type: 'input',
    required: true,
    label: 'Input 1',
  }
}
See Field.FIELD_COMPONENTS for supported types
No other props are necessary.
However, if you need to render fields nested within other elements (e.g. <div><Input /></div>),
you can use custom layout fields. Here's an example:
<Form
  fields={{
    field1: {
      skipRendering: true,
      type: 'input',
      required: true,
      label: 'Input 1',
    }
  }}
  renderFields={(fieldProps) => <div><Field {...fieldProps} id="field1" /></div>}
/>
A few things of note:
1. The field object in Form.props.fields has skipRendering = true
2. The fieldProps argument in renderFields should be spread on the Field component
3. The id prop of the Field must match the key in Form.props.fields
4. All keys in Form.props.fields.field1 are passed as props to the Field component, and then to the underlying Input
*/

const DEFAULT_VALUES = {
  dropdown: "",
  input: "",
  textarea: "",
  taginput: [],
  selector: "",
  checkbox: false,
}

const getDefaultValues = (fields) => {
  const defaultValues = new Map()
  fields.forEach((field) => {
    const defaultValue = field.defaultValue ? field.defaultValue : DEFAULT_VALUES[field.type]
    defaultValues.set(field.id, defaultValue)
  })

  return defaultValues
}

const getDefaultErrors = (fields) => {
  const defaultErrors = new Map()
  fields.forEach((field) => {
    defaultErrors.set(field.id, "")
  })

  return defaultErrors
}

const validateRequired = (field, values, errors) => {
  const fieldIsNull = !values.get(field.id) || values.get(field.id).length === 0
  if (field.required && fieldIsNull) {
    errors.set(field.id, ERRORS.required)
    return true
  }
}

const validateFields = (fields, values) => {
  const errors = new Map()
  let formIsValid = true
  fields.forEach((field) => {
    const hasError = validateRequired(field, values, errors)

    if (hasError) {
      formIsValid = false
    }
  })

  return [formIsValid, errors]
}

const Form = ({ onSubmit, fields, renderFooter, renderFields }) => {
  const formRef = useRef(null)
  const fieldsAsArray = Object.keys(fields).map((key) => ({
    ...fields[key],
    id: key,
  }))
  const [values, setValues] = useState(getDefaultValues(fieldsAsArray))
  const [errors, setErrors] = useState(getDefaultErrors(fieldsAsArray))
  const [isSubmitting, setSubmitting] = useState(false)

  const handleFieldChange = (id, newValue) => {
    const newValues = new Map(values)
    newValues.set(id, newValue)
    setValues(newValues)

    const newErrors = new Map(errors)
    newErrors.set(id, "")
    setErrors(newErrors)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    const [isValid, newErrors] = validateFields(fieldsAsArray, values)

    if (!isValid) {
      setErrors(newErrors)
      if (formRef.current && formRef.current.scrollIntoView) {
        formRef.current.scrollIntoView()
      }
    } else {
      setSubmitting(true)
      await onSubmit(values)
    }
  }

  return (
    <form onSubmit={handleSubmit} className={styles.container} ref={formRef}>
      {fieldsAsArray.map((field) => {
        if (!field.skipRendering) {
          return (
            <Field
              {...field}
              key={field.id}
              values={values}
              errors={errors}
              onChange={handleFieldChange}
              fields={fields}
            />
          )
        }
      })}

      {renderFields({ values, errors, onChange: handleFieldChange, fields })}

      {renderFooter({ onSubmit: handleSubmit, isSubmitting, values })}
    </form>
  )
}

Form.defaultProps = {
  onSubmit: () => {},
  fields: {},
  renderFooter: ({ onSubmit }) => <Button label="Submit" onClick={onSubmit} />,
  renderFields: () => null,
}

export default Form
